/**
 * Search — the funnel readout: Base bar + one bar per step (passing count),
 * with the three-way split and warnings underneath each. Pure display; step
 * clicks bubble up (drill-in).
 */
import { AlertTriangle } from 'lucide-react';
import BarRow from '../reports/BarRow';
import { SummaryStat } from '../reports/StatBlocks';
import type { SearchStepStats, SearchStepDef } from '../../types';
import { describeStep, WARNING_LABELS, type DescribeCtx } from '../../pages/search/searchLib';

interface Props {
  baseTotal: number | null;
  steps: SearchStepStats[];
  stepDefs?: SearchStepDef[];
  finalTotal: number | null;
  onStepClick?: (stepId: string) => void;
  activeStepId?: string | null;
  ctx?: DescribeCtx;
}

export default function FunnelRail({ baseTotal, steps, stepDefs, finalTotal, onStepClick, activeStepId, ctx }: Props) {
  const max = Math.max(1, baseTotal ?? 0);
  const pct = baseTotal && finalTotal !== null && baseTotal > 0
    ? `${((finalTotal / baseTotal) * 100).toFixed(1)}%`
    : '—';

  const labelFor = (s: SearchStepStats, i: number): string => {
    const def = (stepDefs || [])[i];
    if (def) return describeStep(def, ctx);
    return s.label || `Step ${i + 1}`;
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <SummaryStat label="Base population" value={baseTotal === null ? '—' : baseTotal.toLocaleString()} />
        <SummaryStat label="Final list" value={finalTotal === null ? '—' : finalTotal.toLocaleString()} highlight />
        <SummaryStat label="Of base" value={pct} />
      </div>
      <div className="rounded-xl border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-800">
        <BarRow label="Base" count={baseTotal ?? 0} max={max} color="bg-gray-300 dark:bg-gray-600" />
        {steps.map((s, i) => (
          <div key={s.step_id}>
            <BarRow
              label={`${i + 1}. ${labelFor(s, i)}`}
              count={s.passing}
              max={max}
              hint={s.no_data > 0 ? `${s.no_data.toLocaleString()} no data` : null}
              color={s.mode === 'exclude' ? 'bg-rose-400' : 'bg-primary-500'}
              onClick={onStepClick ? () => onStepClick(s.step_id) : undefined}
              active={activeStepId === s.step_id}
            />
            {s.warnings.length > 0 && (
              <div className="flex items-start gap-1 px-2 pb-1 text-xs text-amber-600 dark:text-amber-400">
                <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                <span>{s.warnings.map((w) => WARNING_LABELS[w] || w).join(' · ')}</span>
              </div>
            )}
          </div>
        ))}
        {steps.length === 0 && (
          <p className="px-2 py-3 text-sm text-gray-400 dark:text-gray-500">
            Add steps to narrow the base population — each level reports who's in, who's out, and who has no data.
          </p>
        )}
      </div>
    </div>
  );
}
